import { MapPin, Maximize, ZoomIn, ZoomOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ComponentProps } from "react";
import TrackLayoutVisualization from "@/components/tracks/TrackLayoutVisualization";
import type { TrackMapControls } from "@/components/tracks/useTrackMapZoom";
import type { TrackType } from "@/types";
import { TrackTypeBadge } from "@/components/tracks/TrackTypeBadge";

type TrackLayoutVizProps = ComponentProps<typeof TrackLayoutVisualization>;
type PositionTrail = TrackLayoutVizProps["positionTrail"];

interface TrackMapPanelProps {
  trackName?: string;
  trackType?: TrackType;
  mapControls: TrackMapControls | null;
  onGetMapControls: (controls: TrackMapControls | null) => void;
  trackLayout: TrackLayoutVizProps["layoutData"] | null | undefined;
  isLoading: boolean;
  error: unknown;
  vehiclePosition: { lat: number; lng: number; speed?: number } | null;
  positionTrail: PositionTrail;
}

export default function TrackMapPanel({
  trackName,
  trackType,
  mapControls,
  onGetMapControls,
  trackLayout,
  isLoading,
  error,
  vehiclePosition,
  positionTrail,
}: TrackMapPanelProps) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="flex-1 bg-[#0d0d14] overflow-hidden min-h-[200px] flex flex-col">
      <div className="flex items-center justify-between px-3 py-2 bg-[#111827] border-b border-[#1a1a2e] flex-shrink-0">
        <span className="text-xs font-semibold uppercase tracking-widest text-[#4b5563]">{t("pages:sessions.analytics.trackMap")}</span>
        <div className="flex items-center gap-1.5 min-w-0">
          {trackName && (
            <span className="text-xs text-[#374151] truncate">{trackName}</span>
          )}
          <span
            className="text-xs tabular-nums text-[#4b5563]"
            style={{ minWidth: 36, textAlign: "right" }}
            data-testid="track-map-zoom-percent"
          >
            {mapControls?.zoomPercent ?? 100}%
          </span>
          <button
            onClick={() => mapControls?.zoomIn()}
            disabled={!mapControls || mapControls.zoomPercent >= 800}
            className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5"
            style={{ color: "#4b5563" }}
            title={t("pages:tracks.layout.zoomIn")}
            aria-label={t("pages:tracks.layout.zoomIn")}
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => mapControls?.zoomOut()}
            disabled={!mapControls || mapControls.zoomPercent <= 100}
            className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5"
            style={{ color: mapControls?.isZoomed ? "#4b5563" : "#2d2d3a" }}
            title={t("pages:tracks.layout.zoomOut")}
            aria-label={t("pages:tracks.layout.zoomOut")}
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => mapControls?.reset()}
            disabled={!mapControls || !mapControls.isZoomed}
            className="flex min-h-11 min-w-11 items-center justify-center rounded lg:min-h-0 lg:min-w-0 lg:p-1.5"
            style={{ color: mapControls?.isZoomed ? "#4b5563" : "#2d2d3a" }}
            title={t("pages:tracks.layout.fitToView")}
            aria-label={t("pages:tracks.layout.fitToView")}
          >
            <Maximize className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      {isLoading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="h-5 w-5 border-2 border-[#3b82f6] border-t-transparent rounded-full mx-auto mb-2 animate-spin" />
            <p className="text-sm text-[#4b5563]">{t("pages:sessions.analytics.loadingTrackLayout")}</p>
          </div>
        </div>
      ) : error || !trackLayout ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-4">
            <MapPin className="h-8 w-8 text-[#374151] mx-auto mb-2 opacity-40" />
            <p className="text-sm font-medium text-[#4b5563] mb-1">{trackName}</p>
            {trackType && <TrackTypeBadge type={trackType} />}
            <p className="text-xs text-[#2d2d3a] mt-2">{t("pages:sessions.analytics.layoutNotAvailable")}</p>
          </div>
        </div>
      ) : (
        <TrackLayoutVisualization
          layoutData={trackLayout}
          className="w-full h-full"
          theme="dark"
          enableZoom
          onGetMapControls={onGetMapControls}
          showSectors={true}
          showPitlane={true}
          currentPosition={
            vehiclePosition
              ? {
                  lat: vehiclePosition.lat,
                  lng: vehiclePosition.lng,
                }
              : undefined
          }
          currentSpeed={vehiclePosition?.speed}
          positionTrail={positionTrail}
          showSpeedColors={true}
        />
      )}
    </div>
  );
}
